import React, { useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import styles from './styles.module.scss'

interface SubmitButtonProps {
    onClick: () => void | Promise<void>
    children: React.ReactNode
    disabled?: boolean
    /** Greyed-out look, but still clickable (e.g. to show a complete-the-form message). */
    looksDisabled?: boolean
    isSubmitting?: boolean
    /** Shows the spinner and loadingText, disabled. Overrides the button's own loading state when set. */
    loading?: boolean
    /** Text beside the spinner while loading */
    loadingText?: React.ReactNode
    /** Extra class, e.g. to centre or stretch it outside the form's nav row */
    className?: string
}

const SubmitButton: React.FC<SubmitButtonProps> = ({
    onClick,
    children,
    disabled = false,
    looksDisabled = false,
    isSubmitting = false,
    loading,
    loadingText = 'Submitting…',
    className,
}) => {
    const [ownLoading, setIsLoading] = useState(false)
    const isLoading = loading ?? ownLoading
    const buttonRef = useRef<HTMLButtonElement>(null)

    // The glass's specular highlight follows the pointer (CSS reads --gx/--gy);
    // set directly on the element so moving doesn't re-render
    const trackPointer = (e: React.PointerEvent<HTMLButtonElement>) => {
        const el = buttonRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        el.style.setProperty('--gx', `${((e.clientX - rect.left) / rect.width) * 100}%`)
        el.style.setProperty('--gy', `${((e.clientY - rect.top) / rect.height) * 100}%`)
    }

    const handleClick = async () => {
        if (isSubmitting) {
            setIsLoading(true)
        }

        try {
            await onClick()
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <button
            // Never a form submit: the claim form's <form> is only there for autofill
            type="button"
            ref={buttonRef}
            onPointerMove={trackPointer}
            className={`${styles.submitButton} ${isLoading ? styles.loading : ''} ${looksDisabled ? styles.looksDisabled : ''} ${className || ''}`}
            onClick={handleClick}
            disabled={isLoading || disabled}
            aria-busy={isLoading}
            aria-disabled={looksDisabled || disabled}
        >
            {/* Above the glass's highlight layers */}
            <span className={styles.label}>
                {isLoading ? (
                    <>
                        <Loader2 className={styles.spinner} aria-hidden="true" />
                        {loadingText}
                    </>
                ) : (
                    children
                )}
            </span>
        </button>
    )
}

export default SubmitButton
